import { ArrowUturnRightIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import useChatActions from 'src/components/Chat/Chat.actions';
import { useChatContext } from 'src/components/Chat/Chat.hook';
import { tAttachment, tChatEvent } from 'src/components/Chat/Chat.types';
import useMessageBubbleMenu from '../MessageBubbleMenu/MessageBubbleMenu.hook';
import BubbleSenderLabel from '../MessageBubble/components/BubbleSenderLabel/BubbleSenderLabel.component';
import { isRenderableAttachment } from '../MessageBubble/components/MessageByContentType.functions';
import useSenderTitle from '../MessageBubble/hooks/useSenderTitle.hook';
import { Lightbox } from '../MessageBubble/Lightbox/Lightbox.component';
import useBubbleDate from '../../../../bubbleDate/useBubbleDate.hook';
import ScheduledLegend from '../ScheduledLegend/ScheduledLegend.component';
import {
  albumCaption,
  albumLayout,
  albumState,
} from './AlbumBubble.functions';
import AlbumTile from './components/AlbumTile/AlbumTile.component';
import '../MessageBubble/MessageBubbleV2.styles.scss';
import './AlbumBubble.styles.scss';

export type tAlbumBubbleProps = {
  /** The album's photos in display order, each its own message (INT-1227). */
  album: readonly tChatEvent[];
  innerRef: React.RefObject<HTMLDivElement>;
  /**
   * Publishes the bubble's node under every photo's event id, so a search hit
   * on any of them can be jumped to (SRE-3328). Same contract as EventBubble's.
   */
  registerNode?: (eventId: string, node: HTMLElement | null) => void;
  /** INT-1505: the scheduled card one of the photos was sent from, if any. */
  scheduledOrigin?: tChatEvent;
};

const isDefined = <T,>(value: T | undefined): value is T => value !== undefined;

/**
 * INT-1227: photos sent together, in one bubble laid out like WhatsApp Web's
 * album. The bubble carries what the photos share (sender, time, ticks,
 * caption); each tile keeps its own message's menu, reaction, star and
 * selection. Clicking a photo opens the viewer on it, with the whole album to
 * swipe through.
 */
function AlbumBubble(props: tAlbumBubbleProps) {
  const { album, innerRef, registerNode, scheduledOrigin } = props;
  const [first] = album;
  const fromMe = Boolean(first.message?.fromMe);
  const { t } = useTranslation('chat/chat');
  const bubbleDate = useBubbleDate();
  const { isResending, resendIds } = useChatContext();
  const { showCheckStatus } = useMessageBubbleMenu();
  const { decryptAttachment } = useChatActions();
  const { title, isLeadAgent } = useSenderTitle(first.createdBy);
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [photos, setPhotos] = useState<ReadonlyMap<string, tAttachment>>(
    () => new Map(),
  );

  // One node, every photo's id: the hidden photos have no bubble of their own.
  // Keyed on the ids so a regrouping that keeps the same photos keeps the ref.
  const photoIds = album.map((event) => event.id).join('|');
  const setNode = useCallback(
    (node: HTMLDivElement | null) => {
      if (innerRef)
        (innerRef as React.MutableRefObject<HTMLDivElement | null>).current =
          node;
      photoIds.split('|').forEach((id) => registerNode?.(id, node));
    },
    [innerRef, registerNode, photoIds],
  );

  // Decrypt only when a photo or its file changes, not on every regrouping.
  const albumKey = album
    .map((event) => `${event.id}:${event.content?.attachments?.[0]?.url ?? ''}`)
    .join('|');

  useEffect(() => {
    let cancelled = false;

    const decryptAll = async () => {
      const entries = await Promise.all(
        album.map(async (event) => {
          const attachment = event.content?.attachments?.[0];
          if (!attachment) return undefined;
          try {
            const decrypted = await decryptAttachment(attachment);
            return isRenderableAttachment(decrypted)
              ? ([event.id, decrypted] as const)
              : undefined;
          } catch (error) {
            LLog.error(
              {
                component: LOG_COMPONENTS.UI,
                method: 'AlbumBubble.decryptAll',
                operation: LOG_OPERATIONS.ERROR,
              },
              'Error decrypting an album photo',
              error as unknown as Error,
            );
            return undefined;
          }
        }),
      );
      // A newer album (a photo arrived, or the chat changed) owns the state now.
      if (!cancelled) setPhotos(new Map(entries.filter(isDefined)));
    };

    decryptAll();
    return () => {
      cancelled = true;
    };
  }, [albumKey]);

  const layout = albumLayout(album.length);
  const tiles = isExpanded ? album : album.slice(0, layout.tiles);
  const caption = albumCaption(album);
  const state = albumState(album);
  const isForwarded = Boolean(first.message?.isForwarded);
  const isAnySelected = album.some((event) => resendIds.includes(event.id));

  // The viewer swipes through the photos that loaded, in album order.
  const viewerPhotos = useMemo(
    () => album.map((event) => photos.get(event.id)).filter(isDefined),
    [album, photos],
  );
  const viewerOpenAt =
    viewerIndex === null
      ? 0
      : album.slice(0, viewerIndex).filter((event) => photos.has(event.id))
          .length;

  return (
    <div
      className={classNames('MessageBubbleV2 AlbumBubble', {
        'MessageBubbleV2--resending': isResending,
      })}
      ref={setNode}
      data-testid="album-bubble"
    >
      {isResending && (
        // Each photo has its own checkbox; this cell keeps the column.
        <div
          className={classNames('MessageBubbleV2__CheckContainer', {
            'bg-primary/20': isAnySelected,
          })}
        />
      )}

      <div
        className={classNames('MessageBubbleV2__Wrapper', {
          'bg-primary/20': isAnySelected,
          'col-span-11': isResending,
          'col-span-12': !isResending,
          'chat-end justify-end': fromMe,
          'chat-start': !fromMe,
        })}
      >
        <div
          className={classNames('flex items-end gap-1', {
            'flex-row-reverse': fromMe,
          })}
        >
          <div
            className={classNames(
              'MessageBubbleV2__Container AlbumBubble__Container',
              {
                'MessageBubbleV2__Container--outgoing': fromMe && !isLeadAgent,
                'MessageBubbleV2__Container--slb': isLeadAgent,
                'MessageBubbleV2__Container--incoming': !fromMe,
                'MessageBubbleV2__Container--tail-start': !fromMe,
                'MessageBubbleV2__Container--tail-end': fromMe,
              },
            )}
          >
            <div
              className={classNames('MessageBubbleV2__Header', {
                'justify-end': !fromMe,
                'justify-start': fromMe,
              })}
            >
              {fromMe && !isForwarded ? (
                <BubbleSenderLabel title={title} isLeadAgent={isLeadAgent} />
              ) : null}

              {isForwarded && (
                <span className="MessageBubbleV2__Forwarded MessageBubbleV2__accent">
                  <ArrowUturnRightIcon className="MessageBubbleV2__Forwarded__Icon" />
                  {t('messageBubble.forwarded')}
                </span>
              )}

              <div
                className={classNames('MessageBubbleV2__Header__Date', {
                  'flex-row-reverse': !fromMe,
                  'justify-end': fromMe,
                  MessageBubbleV2__accent: fromMe,
                  'justify-start': !fromMe,
                })}
              >
                <span>{bubbleDate.stamp(first.createdAt)}</span>
              </div>

              {fromMe && state && showCheckStatus(state)}
            </div>

            <div
              className={classNames(
                'AlbumBubble__Grid',
                `AlbumBubble__Grid--${isExpanded ? 'all' : layout.variant}`,
              )}
            >
              {tiles.map((event, index) => (
                <AlbumTile
                  key={event.id}
                  event={event}
                  attachment={photos.get(event.id)}
                  position={index + 1}
                  total={album.length}
                  overflow={
                    !isExpanded && index === layout.tiles - 1
                      ? layout.overflow
                      : 0
                  }
                  onOpen={() => setViewerIndex(index)}
                  onShowAll={() => setIsExpanded(true)}
                />
              ))}
            </div>

            {isExpanded && layout.overflow > 0 && (
              <button
                type="button"
                className="AlbumBubble__Toggle MessageBubbleV2__accent"
                onClick={() => setIsExpanded(false)}
              >
                {t('album.showLess')}
              </button>
            )}

            {caption ? (
              <p
                className="AlbumBubble__Caption"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(caption) }}
              />
            ) : null}

            {scheduledOrigin && <ScheduledLegend followUp={scheduledOrigin} />}
          </div>
        </div>
      </div>

      {viewerIndex !== null && viewerPhotos.length > 0 && (
        <Lightbox
          attachments={viewerPhotos}
          openAt={viewerOpenAt}
          isLightboxOpen
          onClose={() => setViewerIndex(null)}
        />
      )}
    </div>
  );
}

export default React.memo(AlbumBubble);
